import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/App";
import { eduApi, US_STATES } from "@/lib/edu";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Search, ExternalLink, MapPin, Users, DollarSign, GraduationCap,
  Loader2, ChevronLeft, ChevronRight, AlertCircle, Filter, X, BookOpen,
} from "lucide-react";

const PER_PAGE = 20;

const initialFilters = {
  q: "",
  state: "",
  city: "",
  ownership: "",
  school_type: "",
  tuition_max: "",
  student_size_min: "",
  student_size_max: "",
};

export default function CollegesDirectory() {
  const { t, language } = useLanguage();
  const [filters, setFilters] = useState(initialFilters);
  const [pending, setPending] = useState(initialFilters);
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await eduApi.search({
        ...filters,
        student_size_min: filters.student_size_min ? Number(filters.student_size_min) : undefined,
        student_size_max: filters.student_size_max ? Number(filters.student_size_max) : undefined,
        tuition_max: filters.tuition_max ? Number(filters.tuition_max) : undefined,
        page,
        per_page: PER_PAGE,
      });
      setData(res);
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.detail || e.message;
      if (status === 503) {
        setError({ type: "config", msg });
      } else {
        setError({ type: "other", msg });
      }
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  const total = data?.total || 0;
  const totalPages = Math.ceil(total / PER_PAGE);
  const showingFrom = total === 0 ? 0 : page * PER_PAGE + 1;
  const showingTo = Math.min((page + 1) * PER_PAGE, total);

  return (
    <div className="space-y-5" data-testid="colleges-directory">
      {/* Search bar */}
      <div className="glass-card rounded-2xl p-4 flex flex-col md:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
          <Input
            value={pending.q}
            onChange={(e) => setPending({ ...pending, q: e.target.value })}
            onKeyDown={(e) => { if (e.key === "Enter") { setFilters(pending); setPage(0); } }}
            placeholder={t("ابحث باسم الجامعة أو الكلية…", "Search by school name…")}
            className="bg-slate-900 border-slate-700 text-white pr-10"
            data-testid="edu-search-input"
          />
        </div>
        <Button
          variant="outline"
          onClick={() => setShowFilters((v) => !v)}
          className="border-slate-700 text-white hover:bg-slate-800"
          data-testid="edu-toggle-filters"
        >
          <Filter size={16} className="ml-2" />
          {t("الفلاتر", "Filters")}
        </Button>
        <Button onClick={() => { setFilters(pending); setPage(0); }} className="btn-primary" data-testid="edu-search-submit">
          {t("بحث", "Search")}
        </Button>
      </div>

      {/* Filters panel */}
      {showFilters && (
        <div className="glass-card rounded-2xl p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <FilterSelect
            label={t("الولاية", "State")}
            value={pending.state}
            onChange={(v) => setPending({ ...pending, state: v })}
            options={[["", t("كل الولايات", "All states")], ...US_STATES.map(([c, n]) => [c, `${c} — ${n}`])]}
            testid="edu-filter-state"
          />
          <FilterInput
            label={t("المدينة", "City")}
            value={pending.city}
            onChange={(v) => setPending({ ...pending, city: v })}
            placeholder="e.g. Houston"
            testid="edu-filter-city"
          />
          <FilterSelect
            label={t("نوع المؤسسة", "Ownership")}
            value={pending.ownership}
            onChange={(v) => setPending({ ...pending, ownership: v })}
            options={[
              ["", t("الكل", "All")],
              ["public", t("حكومية", "Public")],
              ["private", t("خاصة", "Private")],
            ]}
            testid="edu-filter-ownership"
          />
          <FilterSelect
            label={t("نوع الدراسة", "School type")}
            value={pending.school_type}
            onChange={(v) => setPending({ ...pending, school_type: v })}
            options={[
              ["", t("الكل", "All")],
              ["community_college", t("كلية مجتمعية (سنتين)", "Community College")],
              ["university", t("جامعة (4 سنوات+)", "University")],
            ]}
            testid="edu-filter-type"
          />
          <FilterInput
            label={t("الميزانية القصوى للرسوم (سنوياً)", "Max tuition / year ($)")}
            type="number"
            value={pending.tuition_max}
            onChange={(v) => setPending({ ...pending, tuition_max: v })}
            placeholder="e.g. 25000"
            testid="edu-filter-budget"
          />
          <div className="grid grid-cols-2 gap-2">
            <FilterInput
              label={t("حد أدنى لعدد الطلاب", "Min students")}
              type="number"
              value={pending.student_size_min}
              onChange={(v) => setPending({ ...pending, student_size_min: v })}
              placeholder="1000"
              testid="edu-filter-size-min"
            />
            <FilterInput
              label={t("حد أعلى", "Max")}
              type="number"
              value={pending.student_size_max}
              onChange={(v) => setPending({ ...pending, student_size_max: v })}
              placeholder="50000"
              testid="edu-filter-size-max"
            />
          </div>
          <div className="md:col-span-2 lg:col-span-3 flex gap-2 justify-end pt-2">
            <Button variant="ghost" onClick={() => { setPending(initialFilters); setFilters(initialFilters); setPage(0); }} className="text-slate-400">
              <X size={14} className="ml-2" />{t("مسح الفلاتر", "Reset")}
            </Button>
            <Button onClick={() => { setFilters(pending); setPage(0); }} className="btn-primary" data-testid="edu-apply-filters">
              {t("تطبيق", "Apply")}
            </Button>
          </div>
        </div>
      )}

      {/* Source note */}
      <p className="text-xs text-slate-500 px-1">
        {t(
          "المصدر: قاعدة بيانات وزارة التعليم الأمريكية (College Scorecard) — بيانات رسمية وحديثة.",
          "Source: U.S. Department of Education College Scorecard — official and up-to-date.",
        )}
      </p>

      {/* Status / Results */}
      {error && (
        <ErrorState type={error.type} msg={error.msg} language={language} t={t} />
      )}

      {loading && !data && (
        <div className="glass-card rounded-2xl p-12 flex flex-col items-center gap-3" data-testid="edu-loading">
          <Loader2 className="animate-spin text-purple-400" size={28} />
          <p className="text-slate-400 text-sm">{t("جارٍ تحميل المؤسسات…", "Loading schools…")}</p>
        </div>
      )}

      {!error && data && (
        <>
          <div className="flex justify-between items-center text-sm text-slate-400 px-1">
            <span data-testid="edu-result-count">
              {t(
                `${total.toLocaleString("ar")} مؤسسة — عرض ${showingFrom}–${showingTo}`,
                `${total.toLocaleString()} schools — showing ${showingFrom}–${showingTo}`,
              )}
            </span>
            {loading && <Loader2 size={14} className="animate-spin text-purple-400" />}
          </div>

          {data.results.length === 0 ? (
            <div className="glass-card rounded-2xl p-12 text-center" data-testid="edu-empty">
              <BookOpen className="mx-auto text-slate-500 mb-3" size={32} />
              <p className="text-slate-300">{t("لا توجد نتائج مطابقة. جرّب توسيع الفلاتر.", "No matches. Try widening your filters.")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {data.results.map((s) => (
                <SchoolCard key={s.id} s={s} language={language} t={t} />
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-3" data-testid="edu-pagination">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 0 || loading}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="border-slate-700 text-white hover:bg-slate-800"
                data-testid="edu-page-prev"
              >
                <ChevronRight size={16} className="ml-1" />{t("السابق", "Prev")}
              </Button>
              <span className="text-sm text-slate-300 px-3">
                {t(`صفحة ${page + 1} من ${totalPages.toLocaleString("ar")}`,
                   `Page ${page + 1} of ${totalPages.toLocaleString()}`)}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages - 1 || loading}
                onClick={() => setPage((p) => p + 1)}
                className="border-slate-700 text-white hover:bg-slate-800"
                data-testid="edu-page-next"
              >
                {t("التالي", "Next")}<ChevronLeft size={16} className="mr-1" />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─────────────── helpers ───────────────

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-xs text-slate-400 mb-1 block">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-slate-900 border border-slate-700 rounded-lg text-white text-sm px-3 py-2 focus:outline-none focus:border-purple-500"
        data-testid={testid}
      >
        {options.map(([v, l]) => (
          <option key={v} value={v}>{l}</option>
        ))}
      </select>
    </label>
  );
}

function FilterInput({ label, value, onChange, placeholder, type = "text", testid }) {
  return (
    <label className="block">
      <span className="text-xs text-slate-400 mb-1 block">{label}</span>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-slate-900 border-slate-700 text-white"
        data-testid={testid}
      />
    </label>
  );
}

function SchoolCard({ s, language, t }) {
  const fmtMoney = (n) => n == null ? "—" : `$${Number(n).toLocaleString()}`;
  const fmtRate  = (n) => n == null ? null : `${Math.round(n * 100)}%`;
  const ownershipBadge = {
    public: { ar: "حكومية", en: "Public", color: "bg-blue-500/15 text-blue-300" },
    private_nonprofit: { ar: "خاصة (غير ربحية)", en: "Private (Nonprofit)", color: "bg-emerald-500/15 text-emerald-300" },
    private_for_profit: { ar: "خاصة (ربحية)", en: "Private (For-Profit)", color: "bg-amber-500/15 text-amber-300" },
  }[s.ownership];
  const typeBadge = {
    community_college: { ar: "كلية مجتمعية", en: "Community College", color: "bg-sky-500/15 text-sky-300" },
    university: { ar: "جامعة", en: "University", color: "bg-purple-500/15 text-purple-300" },
  }[s.school_type];

  return (
    <div className="glass-card rounded-2xl p-5 flex flex-col gap-3" data-testid={`school-card-${s.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-purple-500/15 flex items-center justify-center flex-shrink-0">
          <GraduationCap className="text-purple-300" size={22} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-white text-base leading-tight" data-testid="school-name">{s.name}</h3>
          <p className="text-xs text-slate-400 mt-0.5 inline-flex items-center gap-1">
            <MapPin size={11} />{s.city ? `${s.city}, ` : ""}{s.state}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {typeBadge && <Badge className={`${typeBadge.color}`}>{language === "ar" ? typeBadge.ar : typeBadge.en}</Badge>}
        {ownershipBadge && <Badge className={`${ownershipBadge.color}`}>{language === "ar" ? ownershipBadge.ar : ownershipBadge.en}</Badge>}
      </div>

      <div className="grid grid-cols-3 gap-3 mt-1">
        <Stat
          icon={<Users size={14} className="text-slate-400" />}
          label={t("عدد الطلاب", "Students")}
          value={s.student_size?.toLocaleString() ?? "—"}
        />
        <Stat
          icon={<DollarSign size={14} className="text-emerald-400" />}
          label={t("الرسوم (تقريباً)", "Est. tuition")}
          value={fmtMoney(s.tuition_in_state)}
          sub={s.tuition_out_of_state && s.tuition_out_of_state !== s.tuition_in_state
              ? t(`خارج الولاية: ${fmtMoney(s.tuition_out_of_state)}`,
                   `Out-of-state: ${fmtMoney(s.tuition_out_of_state)}`)
              : null}
        />
        <Stat
          icon={<GraduationCap size={14} className="text-blue-400" />}
          label={t("معدل القبول", "Admission")}
          value={fmtRate(s.admission_rate) ?? "—"}
        />
      </div>

      {s.website && (
        <a
          href={(s.website.startsWith("http") ? s.website : `https://${s.website}`)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 text-sm text-purple-300 hover:text-purple-200 mt-1"
          data-testid="school-website"
        >
          <ExternalLink size={14} />{t("الموقع الرسمي", "Official website")}
        </a>
      )}
    </div>
  );
}

function Stat({ icon, label, value, sub }) {
  return (
    <div className="bg-slate-900/60 rounded-xl px-3 py-2">
      <div className="flex items-center gap-1 text-[10px] text-slate-500 uppercase tracking-wider">{icon}{label}</div>
      <div className="text-sm font-semibold text-white mt-0.5">{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function ErrorState({ type, msg, language, t }) {
  if (type === "config") {
    return (
      <div className="glass-card rounded-2xl p-6 border border-amber-500/30 bg-amber-500/5" data-testid="edu-error-config">
        <div className="flex items-start gap-3">
          <AlertCircle className="text-amber-400 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <p className="font-semibold text-amber-200 mb-1">
              {t("مفتاح College Scorecard غير مفعّل بعد", "College Scorecard API key not yet configured")}
            </p>
            <p className="text-sm text-amber-100/80">
              {t(
                "سيظهر دليل الجامعات بمجرد إضافة المفتاح في إعدادات الخادم (متغير COLLEGE_SCORECARD_API_KEY).",
                "The directory will appear once the COLLEGE_SCORECARD_API_KEY is added to the server environment.",
              )}
            </p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="glass-card rounded-2xl p-6 border border-red-500/30 bg-red-500/5" data-testid="edu-error">
      <div className="flex items-start gap-3">
        <AlertCircle className="text-red-400 flex-shrink-0 mt-0.5" size={20} />
        <div>
          <p className="font-semibold text-red-200 mb-1">{t("تعذّر تحميل البيانات", "Could not load data")}</p>
          <p className="text-sm text-red-100/80">{String(msg).slice(0, 240)}</p>
        </div>
      </div>
    </div>
  );
}
